/**
 * Google Ads Adapter
 * 
 * Adapts standardized event data into Google Ads format.
 * Handles both dynamic remarketing and conversion tracking events.
 * 
 * @since 1.51.0
 */

(function (wpm, $, undefined) {

	/**
	 * Google Ads Data Adapter
	 */
	const GoogleAdsAdapter = {
		
		/**
		 * Main adapt method - routes to specific event adapters
		 * 
		 * @param {object} eventData - Standardized event data
		 * @param {string} eventName - Event name (add_to_cart, view_item, etc.)
		 * @param {object} context - Event context (user, session, page data)
		 * @returns {object|null} Google Ads-formatted data or null if pixel inactive
		 */
		adapt(eventData, eventName, context) {
			// Check if Google Ads is active
			if (jQuery.isEmptyObject(wpmDataLayer?.pixels?.google?.ads?.conversion_ids)) {
				return null;
			}
			
			// Check if marketing tracking is allowed
			if (!wpm.googleConfigConditionsMet({type: "marketing"})) {
				return null;
			}
			
		// Map standardized event names to adapter methods
		const eventMap = {
			'view_item_list': this.adaptViewItemList,
			'add_to_cart': this.adaptAddToCart,
			'view_item': this.adaptViewItem,
			'search': this.adaptSearch,
			'login': this.adaptLogin,
			'purchase': this.adaptPurchase
		};			const adapter = eventMap[eventName];
			
			if (!adapter) {
				pmw.console.log(`No Google Ads adapter for event: ${eventName}`);
				return null;
			}
			
			try {
				return adapter.call(this, eventData, context);
			} catch (e) {
				pmw.console.error(`Google Ads adapter error for ${eventName}:`, e);
				return null;
			}
		},
		
		/**
		 * Get base data structure for Google Ads events
		 */
		getBaseData() {
			const baseData = {
				send_to: wpm.getGoogleAdsConversionIdentifiers()
			};
			
			// Add user_id if available
			if (wpmDataLayer?.user?.id?.raw) {
				baseData.user_id = wpmDataLayer.user.id.raw;
			}
			
			return baseData;
		},
		
		/**
		 * Get product item data for Google Ads
		 */
		getProductItemData(product) {
			return {
				id: product.dyn_r_ids[wpmDataLayer.pixels.google.ads.dynamic_remarketing.id_type],
				google_business_vertical: wpmDataLayer.pixels.google.ads.google_business_vertical
			};
		},
		
		/**
		 * Adapt view_item_list event
		 */
		adaptViewItemList(eventData, context) {
			const product = eventData.product;
			
			// Skip if variations should not be tracked with parent IDs
			if (
				wpmDataLayer?.shop?.variations_output &&
				product.is_variable &&
				wpmDataLayer.pixels.google.ads.dynamic_remarketing.send_events_with_parent_ids === false
			) {
				return null;
			}
			
			// Prevent cached hits
			if (!product) {
				return null;
			}
			
			return {
				...this.getBaseData(),
				items: [this.getProductItemData(product)]
			};
		},
		
		/**
		 * Adapt add_to_cart event
		 */
		adaptAddToCart(eventData, context) {
			const product = eventData.product;
			
			return {
				...this.getBaseData(),
				value: product.quantity * product.price,
				items: [{
					...this.getProductItemData(product),
					quantity: product.quantity,
					price: product.price
				}]
			};
		},
		
		/**
		 * Adapt view_item event
		 */
		adaptViewItem(eventData, context) {
			const product = eventData.product;
			const data = this.getBaseData();
			
			if (product) {
				data.value = (product.quantity ? product.quantity : 1) * product.price;
				data.items = [{
					...this.getProductItemData(product),
					quantity: (product.quantity ? product.quantity : 1),
					price: product.price
				}];
			}
			
			return data;
		},
		
		/**
		 * Adapt search event
		 */
		adaptSearch(eventData, context) {
			const products = [];
			
			Object.values(wpmDataLayer.products).forEach(product => {
				// Skip variations if configured
				if (
					wpmDataLayer?.shop?.variations_output &&
					product.is_variable &&
					wpmDataLayer.pixels.google.ads.dynamic_remarketing.send_events_with_parent_ids === false
				) {
					return;
				}
				
				products.push({
					id: product.dyn_r_ids[wpmDataLayer.pixels.google.ads.dynamic_remarketing.id_type],
					google_business_vertical: wpmDataLayer.pixels.google.ads.google_business_vertical
				});
			});
			
			return {
				...this.getBaseData(),
				items: products
			};
		},
		
		/**
		 * Adapt login event
		 */
		adaptLogin(eventData, context) {
			return this.getBaseData();
		},
		
		/**
		 * Adapt purchase event (order received page)
		 * 
		 * Google Ads has two types of purchase events:
		 * 1. Remarketing purchase event
		 * 2. Conversion tracking event
		 * 
		 * This adapter returns both as separate events
		 */
		adaptPurchase(eventData, context) {
			if (!wpmDataLayer?.order) {
				pmw.console.error('Order data not available for Google Ads Purchase event');
				return null;
			}
			
			// Return both remarketing and conversion data
			return {
				// Remarketing purchase event
				remarketing: {
					send_to: wpm.getGoogleAdsConversionIdentifiers(),
					value: wpmDataLayer.order.value.marketing,
					items: wpm.getGoogleAdsRegularOrderItems()
				},
				
				// Conversion tracking event
				conversion: {
					send_to: wpm.getGoogleAdsConversionIdentifiersWithLabel(),
					transaction_id: wpmDataLayer.order.number,
					value: wpmDataLayer.order.value.marketing,
					currency: wpmDataLayer.order.currency,
					new_customer: wpmDataLayer.order.new_customer,
					...(wpmDataLayer?.order?.value?.ltv?.marketing && {
						customer_lifetime_value: wpmDataLayer.order.value.ltv.marketing
					}),
					...(wpmDataLayer?.user?.id?.raw && {
						user_id: wpmDataLayer.user.id.raw
					}),
					// Cart data if merchant center is configured
					...(wpmDataLayer?.order?.aw_merchant_id && {
						discount: wpmDataLayer.order.discount,
						aw_merchant_id: wpmDataLayer.order.aw_merchant_id,
						aw_feed_country: wpmDataLayer.order.aw_feed_country,
						aw_feed_language: wpmDataLayer.order.aw_feed_language,
						items: wpm.getGoogleAdsRegularOrderItems()
					}),
					// Custom variables
					...(wpmDataLayer?.order?.google?.ads?.custom_variables && 
						wpmDataLayer.order.google.ads.custom_variables
					)
				}
			};
		}
	};

	// Register the adapter
	wpm.registerAdapter('google_ads', GoogleAdsAdapter, {
		isActive: () => {
			return wpmDataLayer?.pixels?.google?.ads?.conversion_ids && 
				Object.keys(wpmDataLayer.pixels.google.ads.conversion_ids).length > 0;
		}
	});

}(window.wpm = window.wpm || {}, jQuery));
